import type { DataGridColumn, SortDescriptor } from "@/components/kit";

import { Button, Chip, Tooltip } from "@heroui/react";
import { Eye } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { CartViewModal } from "./modal/view";

import {
  AttributionFilterSelects,
  SourceCell,
  appliedAttributionFilters,
} from "@/components/attribution";
import {
  AppliedFilters,
  DataTable,
  FiltersPopover,
  SearchBox,
} from "@/components/kit";
import {
  PersonCell,
  Toolbar,
  aed,
  formatDay,
  formatDayTime,
} from "@/components/sales/shared";
import { useAcquisitionOptions } from "@/hooks/api/users/useAcquisitionOptions";
import { AttributionFilters } from "@/types";
import { UserCart } from "@/types/cart";

export interface CartTableProps {
  items: UserCart[];
  page: number;
  totalCarts: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  filters: AttributionFilters;
  onFiltersChange: (filters: AttributionFilters) => void;
  sort: SortDescriptor;
  onSortChange: (sort: SortDescriptor) => void;
}

export const CartListTable = ({
  searchTerm,
  handleSearchTermChange,
  items,
  page,
  totalCarts,
  changePage,
  isLoading,
  limit = 10,
  onLimitChange,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
}: CartTableProps) => {
  const [selectedCart, setSelectedCart] = useState<UserCart | null>(null);
  const [draft, setDraft] = useState<AttributionFilters>(filters);
  const { data: options } = useAcquisitionOptions();
  const applied = appliedAttributionFilters(filters);

  const apply = (next: AttributionFilters) => {
    setDraft(next);
    onFiltersChange(next);
  };

  const handleViewCart = useCallback(
    (cart: UserCart) => setSelectedCart(cart),
    [],
  );

  const columns = useMemo<DataGridColumn<UserCart>[]>(
    () => [
      {
        id: "user",
        header: "Customer",
        isRowHeader: true,
        minWidth: 240,
        cell: (cart) => (
          <PersonCell
            firstName={cart.user.firstName}
            lastName={cart.user.lastName}
            phoneNumber={cart.user.phoneNumber}
          />
        ),
      },
      {
        id: "items",
        header: "Items",
        cell: (cart) => (
          <div className="flex items-center gap-2 tabular-nums">
            {cart.items.length}
            {cart.specialItems.length > 0 ? (
              <Chip color="accent" variant="soft">
                {cart.specialItems.length} special
              </Chip>
            ) : null}
          </div>
        ),
      },
      {
        id: "total",
        header: "Total",
        align: "end",
        minWidth: 260,
        allowsSorting: true,
        cell: (cart) => (
          <div className="flex flex-col items-end py-1 leading-snug tabular-nums">
            <span className="font-medium">
              {cart.total ? aed(cart.total) : "N/A"}
            </span>
            <span className="text-muted text-xs">
              Subtotal {cart.subTotal.toFixed(2)} · VAT {cart.vat.toFixed(2)} ·
              Delivery {cart.deliveryFee.toFixed(2)}
            </span>
          </div>
        ),
      },
      {
        id: "remainingToMinimum",
        header: "To minimum",
        cell: (cart) =>
          cart.remainingToMinimum > 0 ? (
            <Chip color="warning" variant="soft">
              {aed(cart.remainingToMinimum)} left
            </Chip>
          ) : (
            <Chip color="success" variant="soft">
              Minimum met
            </Chip>
          ),
      },
      {
        id: "previousOrders",
        header: "Previous orders",
        align: "end",
        minWidth: 150,
        allowsSorting: true,
        cell: (cart) =>
          cart.orderCount > 0 ? (
            <div className="flex flex-col items-end leading-snug tabular-nums">
              <span className="font-medium">{aed(cart.orderSpend)}</span>
              <span className="text-muted text-xs">
                {cart.orderCount} {cart.orderCount === 1 ? "order" : "orders"}
              </span>
            </div>
          ) : (
            <span className="text-muted">None</span>
          ),
      },
      {
        id: "signedUp",
        header: "Signed up",
        allowsSorting: true,
        cell: (cart) => (
          <span className="tabular-nums">{formatDay(cart.user.createdAt)}</span>
        ),
      },
      {
        id: "source",
        header: "Source",
        minWidth: 140,
        cell: (cart) => <SourceCell attribution={cart.user.acquisition} />,
      },
      {
        id: "updatedAt",
        header: "Updated",
        cell: (cart) => (
          <span className="tabular-nums">{formatDayTime(cart.updatedAt)}</span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 64,
        cell: (cart) => (
          <Tooltip delay={400}>
            <Button
              isIconOnly
              aria-label="View cart"
              size="sm"
              variant="ghost"
              onPress={() => handleViewCart(cart)}
            >
              <Eye className="size-4" />
            </Button>
            <Tooltip.Content>View cart</Tooltip.Content>
          </Tooltip>
        ),
      },
    ],
    [handleViewCart],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar
        end={
          <>
            <SearchBox
              className="w-80"
              placeholder="Search name or phone"
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
            <FiltersPopover
              activeCount={applied.length}
              onApply={() => onFiltersChange(draft)}
              onClearAll={() => apply({})}
            >
              <AttributionFilterSelects
                options={options}
                value={draft}
                onChange={setDraft}
              />
            </FiltersPopover>
          </>
        }
      >
        <AppliedFilters
          filters={applied}
          onClearAll={() => apply({})}
          onRemove={(id) => apply({ ...filters, [id]: undefined })}
        />
      </Toolbar>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No carts found",
          description: "Try a different search or clear the filters.",
        }}
        getRowId={(cart) => cart.id}
        isLoading={isLoading && items.length === 0}
        label="Carts"
        pagination={{
          page,
          pageSize: limit,
          total: totalCarts,
          onPageChange: changePage,
          itemLabel: "carts",
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
        sort={sort}
        onSortChange={onSortChange}
      />

      <CartViewModal
        cart={selectedCart}
        isOpen={selectedCart !== null}
        onClose={() => setSelectedCart(null)}
      />
    </div>
  );
};
